"use client";
import React, { useState, useMemo } from "react";
import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
   Button
} from "@/app/components/ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from "next-intl";

interface GenericTableProps {
   data: unknown;
   title?: string;
}

const extractRowsFromData = (rawData: unknown): Record<string, unknown>[] => {
   if (!rawData) return [];

   if (Array.isArray(rawData)) {
      return rawData.filter(
         (item) => item && typeof item === "object" && !Array.isArray(item)
      ) as Record<string, unknown>[];
   }

   const preferredKeys = ["rows", "items", "data", "coins", "list"];
   if (rawData && typeof rawData === "object") {
      const record = rawData as Record<string, unknown>;
      for (const key of preferredKeys) {
         const value = record[key];
         if (
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === "object" &&
            !Array.isArray(value[0])
         ) {
            return value as Record<string, unknown>[];
         }
      }

      const firstArray = Object.values(record).find(
         (value) =>
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === "object" &&
            !Array.isArray(value[0])
      );

      if (
         Array.isArray(firstArray) &&
         firstArray.length > 0 &&
         typeof firstArray[0] === "object" &&
         !Array.isArray(firstArray[0])
      ) {
         return firstArray as Record<string, unknown>[];
      }
   }

   return [];
};

const formatValue = (value: unknown): React.ReactNode => {
   if (value === null || value === undefined) {
      return <span className="text-muted-foreground">N/A</span>;
   }

   if (typeof value === "boolean") {
      return (
         <span className={value ? "text-green-600" : "text-red-500"}>
            {value ? "Yes" : "No"}
         </span>
      );
   }

   if (typeof value === "number") {
      if (Number.isInteger(value)) {
         return value.toLocaleString("en-US");
      }
      return value.toLocaleString("en-US", {
         minimumFractionDigits: 2,
         maximumFractionDigits: 6
      });
   }

   if (typeof value === "object") {
      if (Array.isArray(value)) {
         return <span className="text-muted-foreground">[{value.length} items]</span>;
      }
      try {
         const jsonStr = JSON.stringify(value, null, 2);
         if (jsonStr.length > 200) {
            return (
               <details className="cursor-pointer">
                  <summary className="text-muted-foreground">
                     {Object.keys(value).length} properties
                  </summary>
                  <pre className="mt-2 text-xs overflow-auto max-h-40 bg-muted p-2 rounded">
                     {jsonStr}
                  </pre>
               </details>
            );
         }
         return (
            <pre className="text-xs bg-muted p-2 rounded overflow-auto">{jsonStr}</pre>
         );
      } catch {
         return String(value);
      }
   }

   return String(value);
};

const formatHeader = (key: string): string => {
   return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
};

export const GenericTable: React.FC<GenericTableProps> = ({ data, title }) => {
   const [isExpanded, setIsExpanded] = useState(true);
   const rows = useMemo(() => extractRowsFromData(data), [data]);
   const translate = useTranslations("Chat.ChatMessageMetadata");
   const headers = useMemo(() => {
      if (!rows || rows.length === 0) return [];

      const firstItem = rows[0];
      if (!firstItem || typeof firstItem !== "object") return [];

      return Object.keys(firstItem);
   }, [rows]);

   const displayRows = rows;

   if (!rows || rows.length === 0 || headers.length === 0) {
      return null;
   }

   return (
      <div className="my-4 w-full">
         <div className="flex justify-end">
            <Button
               variant="ghost"
               size="sm"
               onClick={() => setIsExpanded(!isExpanded)}
               className="gap-2"
            >
               {isExpanded ? (
                  <>
                     <ChevronUp className="h-4 w-4" />
                     {translate("collapse")}
                  </>
               ) : (
                  <>
                     <ChevronDown className="h-4 w-4" />
                     {translate("expand")}
                  </>
               )}
            </Button>
         </div>
         {isExpanded && (
            <div className="overflow-x-auto">
               <Table>
                  <TableHeader>
                     <TableRow>
                        {headers.map((header) => (
                           <TableHead key={header} className="text-left">
                              {formatHeader(header)}
                           </TableHead>
                        ))}
                     </TableRow>
                  </TableHeader>
                  <TableBody>
                     {displayRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                           {headers.map((header) => (
                              <TableCell key={header} className="text-left">
                                 <div className="max-w-[300px] break-words">
                                    {formatValue(row[header])}
                                 </div>
                              </TableCell>
                           ))}
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </div>
         )}
      </div>
   );
};
